<template>
  <div class="min-h-initial max-h-initial overflow-hidden text-gray-800">
    <div
      class="w-full h-full flex flex-col px-[14px] py-[22px] tablet-h:py-[30px] relative overflow-auto"
      :style="componentStyles"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <div
          v-if="!isInitialLoading"
          class="flex items-center mb-[26px] gap-x-2"
        >
          <RouterLink to="/infringement">
            <NewLink type="breadcrumb">Comparison analysis</NewLink>
          </RouterLink>

          <RouterLink :to="`/infringement/report/${reportId}`">
            <NewLink type="breadcrumb">Report</NewLink>
          </RouterLink>
        </div>

        <div
          v-if="isInitialLoading"
          class="mb-[30px]"
          aria-busy="true"
          aria-live="polite"
        >
          <div class="mb-[26px]">
            <div class="flex h-10">
              <template v-for="(tab, index) in loadingTabs" :key="tab.value">
                <div v-if="index > 0" class="w-2.5 border-b border-[#CCC]/50" />
                <div
                  class="h-10 flex items-center border border-[#CCC]/50 rounded-t-xl px-[30px]"
                  :class="
                    activeSide === tab.value
                      ? 'bg-white border-b-white'
                      : 'bg-gray-200/20'
                  "
                >
                  <Skeleton class="size-5 rounded-xs mr-2.5" />
                  <Skeleton
                    :class="
                      tab.value === 'registered'
                        ? 'w-[92px] h-[14px]'
                        : 'w-[174px] h-[14px]'
                    "
                  />
                </div>
              </template>

              <div class="flex-1 border-b border-[#CCC]/50"></div>
            </div>

            <div
              class="h-[120px] rounded-tr-xl rounded-b-xl border border-[#CCC]/50 border-t-0 bg-white px-[22px] py-5"
            >
              <div class="flex h-full items-center gap-x-4">
                <Skeleton class="size-[64px] rounded-md shrink-0" />
                <div class="min-w-0 flex-1">
                  <Skeleton class="w-[240px] max-w-full h-[18px] mb-3" />
                  <Skeleton class="w-[160px] max-w-[70%] h-[14px] mb-2" />
                  <Skeleton class="w-[120px] max-w-[55%] h-[14px]" />
                </div>
              </div>
            </div>
          </div>

          <div class="border border-[#CCC]/50 rounded-xl bg-white">
            <div class="flex items-center gap-x-2.5 px-[30px] py-5">
              <Skeleton class="size-5 rounded-xs" />
              <Skeleton class="w-[210px] max-w-full h-[18px]" />
            </div>

            <div class="w-full h-px bg-[#CCC]/50"></div>

            <div class="px-[30px] py-5">
              <Skeleton class="w-[160px] h-[14px] mb-[25px]" />

              <div class="flex flex-col gap-y-4">
                <Skeleton class="w-[72px] h-[14px]" />
                <Skeleton class="w-full h-[72px] rounded-md" />
                <Skeleton class="w-full h-[360px] rounded-[3px]" />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="hasMatchesContent" class="mb-[30px]">
          <MatchesSectionHeader
            :active-side="activeSide"
            :count="sectionMatchCount"
            :category="category"
            :registered-file="registeredFile"
            :infringing-file="infringingFile"
            @update:active-side="handleActiveSideChange"
          >
            <div class="mt-[25px]">
              <MatchesImage
                v-if="category === MatchCategoryType.Image && imageUrl"
                :image-url="imageUrl"
                :file-name="file?.name"
                :matches="imageMatches"
                :timeline-match-by-id="timelineMatchById"
                @view="handleView"
              />

              <MatchesDocument
                v-else-if="category === MatchCategoryType.Text"
                :key="`text-${activeSide}-${documentSummaryUrl}`"
                :summary-url="documentSummaryUrl"
                :timeline-match-by-id="timelineMatchById"
                @update:count="documentMatchCount = $event"
                @view="handleView"
              />

              <MatchesTranscript
                v-else-if="category === MatchCategoryType.Transcript"
                :matches="transcriptMatches"
                :timeline-match-by-id="timelineMatchById"
                @view="handleView"
              />
            </div>
          </MatchesSectionHeader>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MatchesSectionHeader from '@/features/infringement/components/matches/MatchesSectionHeader.vue'
import type { MatchesSide } from '@/features/infringement/components/matches/MatchesSectionHeader.vue'
import MatchesImage from '@/features/infringement/components/matches/MatchesImage.vue'
import MatchesDocument from '@/features/infringement/components/matches/MatchesDocument.vue'
import MatchesTranscript from '@/features/infringement/components/matches/MatchesTranscript.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import type {
  TimelineMatch,
  TimelineMatchType,
} from '@/features/infringement/components/timeline-types'
import NewLink from '@/shared/components/NewLink.vue'
import { graphql } from '@/shared/graphql/router'
import {
  MatchCategoryType,
  ScoreLevel,
  type ReportFile,
} from '@/shared/graphql/router/graphql'
import { Skeleton } from '@dacr/ui'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const { availableHeight } = useAvailableHeight()

const reportId = computed(() => route.params.id as string)

const activeSide = ref<MatchesSide>(
  route.query.side === 'infringing' ? 'infringing' : 'registered'
)
const documentMatchCount = ref(0)
const loadingTabs: { value: MatchesSide }[] = [
  { value: 'registered' },
  { value: 'infringing' },
]

watch(
  () => route.query.side,
  (side) => {
    activeSide.value = side === 'infringing' ? 'infringing' : 'registered'
  }
)

const category = computed<MatchCategoryType | null>(() => {
  switch (route.params.category) {
    case 'image':
      return MatchCategoryType.Image
    case 'text':
      return MatchCategoryType.Text
    case 'transcript':
      return MatchCategoryType.Transcript
    default:
      return null
  }
})

const { result, loading: queryLoading } = useQuery(
  graphql(`
    query ReportMatches($id: ID!) {
      report(id: $id) {
        id
        copyright {
          id
          name
          type
          asset {
            previewImageUrl
          }
          copyright {
            id
            registrations {
              timestamp
            }
          }
        }
        infringingFile {
          id
          name
          type
          asset {
            previewImageUrl
          }
        }
        matches {
          categories {
            type
            summary {
              copyright
              infringingFile
            }
            breakdown {
              ...MatchBreakdown
              copyright {
                ... on ImageMatch {
                  bbox: coords
                }
                ... on TranscriptMatch {
                  transcriptUrl: url
                }
              }
              infringingFile {
                ... on ImageMatch {
                  bbox: coords
                }
                ... on TranscriptMatch {
                  transcriptUrl: url
                }
              }
            }
          }
        }
      }
    }
  `),
  () => ({ id: reportId.value }),
  { clientId: 'router' }
)

const report = computed(() => result.value?.report)

const isInitialLoading = computed(() => queryLoading.value && !report.value)

const registeredFile = computed<ReportFile | null>(
  () => (report.value?.copyright as ReportFile | undefined) ?? null
)

const infringingFile = computed<ReportFile | null>(
  () => (report.value?.infringingFile as ReportFile | undefined) ?? null
)

const file = computed<ReportFile | null>(() =>
  activeSide.value === 'registered'
    ? registeredFile.value
    : infringingFile.value
)

const activeCategory = computed(() => {
  if (!report.value || !category.value) return null
  return report.value.matches.categories.find((c) => c.type === category.value)
})

const categoryBreakdown = computed(() => activeCategory.value?.breakdown ?? [])

const documentSummaryUrl = computed(() => {
  const summary = activeCategory.value?.summary
  if (!summary) return null

  return activeSide.value === 'registered'
    ? summary.copyright
    : summary.infringingFile
})

const getMatchLevel = (match: (typeof categoryBreakdown.value)[number]) => {
  return 'level' in match.infringingFile
    ? match.infringingFile.level
    : undefined
}

const sideMatches = computed(() =>
  categoryBreakdown.value.map((b) => {
    if (activeSide.value === 'infringing') {
      return b.infringingFile
    }

    return {
      ...b.copyright,
      level: getMatchLevel(b),
    }
  })
)

const imageMatches = computed(() =>
  sideMatches.value.filter(
    (m): m is Extract<(typeof sideMatches.value)[number], { url: string }> =>
      '__typename' in m && m.__typename === 'ImageMatch'
  )
)

const transcriptMatches = computed(() =>
  sideMatches.value.filter(
    (
      m
    ): m is Extract<
      (typeof sideMatches.value)[number],
      { transcript: unknown }
    > => '__typename' in m && m.__typename === 'TranscriptMatch'
  )
)

const totalMatches = computed(() => sideMatches.value.length)

const TYPENAME_TO_TIMELINE_TYPE: Record<string, TimelineMatchType> = {
  ImageMatch: 'Image',
  TextMatch: 'Text',
  TranscriptMatch: 'Transcript',
  AudioMatch: 'Audio',
  VideoMatch: 'Video',
}

const timelineMatchById = computed(() => {
  const map = new Map<string, TimelineMatch>()

  categoryBreakdown.value.forEach((b, index) => {
    const inf = b.infringingFile as Record<string, unknown> | undefined
    const reg = b.copyright as Record<string, unknown> | undefined
    if (!inf?.id) return

    const typename = String(inf.__typename ?? '')
    const type = TYPENAME_TO_TIMELINE_TYPE[typename] ?? 'Text'

    const infStart = Number(inf.start ?? 0)
    const infEnd = Number(inf.end ?? 0)
    const regStart = Number(reg?.start ?? 0)
    const regEnd = Number(reg?.end ?? 0)
    const location = typeof inf.location === 'string' ? inf.location : ''

    map.set(String(inf.id), {
      id: String(inf.id),
      type,
      level: (inf.level as ScoreLevel) ?? ScoreLevel.High,
      title: String(inf.title ?? type),
      score: Number(inf.score ?? 0),
      number: index + 1,
      location,
      markers: Array.isArray(inf.markers)
        ? (inf.markers as TimelineMatch['markers'])
        : [],
      infringing: {
        start: Number.isFinite(infStart) ? infStart : 0,
        end: Number.isFinite(infEnd) ? infEnd : 0,
      },
      registered: {
        start: Number.isFinite(regStart) ? regStart : 0,
        end: Number.isFinite(regEnd) ? regEnd : 0,
      },
    })
  })

  return map
})

const sectionMatchCount = computed(() => {
  if (category.value === MatchCategoryType.Text) {
    return documentMatchCount.value
  }

  return totalMatches.value
})

const hasMatchesContent = computed(() => {
  if (category.value === MatchCategoryType.Text) {
    return !!documentSummaryUrl.value
  }

  return totalMatches.value > 0
})

watch(documentSummaryUrl, () => {
  documentMatchCount.value = 0
})

const imageUrl = computed(() => file.value?.asset?.previewImageUrl ?? null)

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const { open: openDialog } = useDialog()

const handleView = (matchId: string) => {
  if (!category.value) return
  const breakdown = categoryBreakdown.value.find((b) => {
    const inf = b.infringingFile as { id?: string }
    return inf?.id === matchId
  })
  if (!breakdown) return
  const number = timelineMatchById.value.get(matchId)?.number ?? 1
  openDialog(Dialogs.MatchPair, {
    props: {
      type: category.value,
      match: breakdown,
      number,
    },
  })
}

const handleActiveSideChange = (side: MatchesSide) => {
  activeSide.value = side
  router.replace({
    query: {
      ...route.query,
      side,
    },
  })
}

if (!category.value) {
  router.replace(`/infringement/report/${reportId.value}`)
}
</script>
